import { sampleSkyAuthoredPose } from './115-sky-authored-pose.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';
import { SKY_WORLD } from './86-sky-world-scale.mjs';
import { stepSkyPhysicalOrbit } from './127-sky-orbital-flight.mjs';
const waterSockets = new WeakMap(),
  opaqueMeshes = new WeakMap();

/** Source mesh ownership: the animated water is traversable; all original
 * opaque geometry remains solid for both pilot and camera boom sweeps. */
export function skyOrchidOpaqueMeshes(rig) {
  if (!opaqueMeshes.has(rig))
    opaqueMeshes.set(
      rig,
      Object.freeze(
        rig.meshes.flatMap((mesh, i) => (mesh.name === 'Waterfall_route_curtain' ? [] : [i])),
      ),
    );
  return opaqueMeshes.get(rig);
}

/** Capture a real entry-preserving orbit around the flower's settled source
 * root. Original irrigation assemblies move as whole metre-scale objects so
 * their water curtains cross the side arcs; geometry and UVs remain untouched. */
export function createSkyOrchidOrbit(state, boss) {
  const center = { x: 0, z: -32 + boss.authoredRig.bounds.min[2] };
  const dx = state.x - center.x,
    dz = (state.pilotZ ?? SKY_WORLD.pilotZ) - center.z;
  boss.orchid.orbit = {
    center,
    radius: Math.hypot(dx, dz),
    angle: Math.atan2(dx, dz),
    travelled: 0,
  };
  boss.orchid.orbitHint = 'A/D ORBIT THE FLOWER · W/S ALTITUDE · WASH AT THE SIDE WATERFALLS';
  return boss.orchid.orbit;
}

/** Place the original local water socket on the physical circle. Source left
 * and right assemblies face outward, leaving the inner flower arena open. */
export function skyOrchidWaterTransform(orbit, side, rig, travel = 0) {
  if (!waterSockets.has(rig))
    waterSockets.set(rig, sampleSkyAuthoredPose(rig, 'Irrigate', 0).sockets.Waterfall_wash_center);
  const local = waterSockets.get(rig);
  const angle = (side * Math.PI) / 8,
    rotation = Math.PI / 2 + angle;
  const x = Math.sin(angle) * orbit.radius,
    z = orbit.center.z + Math.cos(angle) * orbit.radius;
  const offsetX = Math.cos(rotation) * local.x + Math.sin(rotation) * local.z;
  const offsetZ = -Math.sin(rotation) * local.x + Math.cos(rotation) * local.z;
  return {
    position: [x - offsetX, skyTerrainHeight(x, z, travel), z - offsetZ],
    quaternion: [0, Math.sin(rotation / 2), 0, Math.cos(rotation / 2)],
    scale: [1, 1, 1],
  };
}

/** Active-time orbital steering reuses Bride's canonical world-space frame.
 * Only real opaque source surfaces block movement; water stays traversable and
 * its exact triangle sweep in122 still exclusively owns washing and baiting. */
export function stepSkyOrchidOrbit(state, input, dt, owners) {
  const boss = state.boss,
    orchid = boss?.orchid;
  if (!orchid?.orbit) return false;
  if (!orchid.rootTransform && boss.age >= 4) {
    orchid.rootTransform = {
      position: [orchid.orbit.center.x, boss.rootTransform.position[1], orchid.orbit.center.z],
      quaternion: [0, 1, 0, 0],
      scale: [1, 1, 1],
    };
  }
  const solids = boss.authoredProps
    .filter((p) => p.orchidWaterfall)
    .map((prop) => ({
      pose: prop.authoredPose,
      meshes: skyOrchidOpaqueMeshes(prop.authoredRig),
      reason: 'orchid-irrigation',
    }));
  solids.push({ pose: boss.authoredPose, reason: 'orchid-flower' });
  return stepSkyPhysicalOrbit(state, input, dt, {
    ...owners,
    orbit: orchid.orbit,
    ceiling: boss.authoredFlightCeiling,
    solids,
  });
}
